import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useDeleteWorkspaceById } from '@/api/workspaces/useDeleteWorkspace';
import { useAppSelector } from '@/app/hooks';
import { Button } from '@/component-library/primitives/Button/Button';
import ChevronIcon from '@/component-library/primitives/Icons/ChevronIcon/ChevronIcon';
import { ProvideToasts, Toast, ToastType } from '@/component-library/primitives/Toast/Toast';
import { Input } from '@/component-library/widgets/Input/Input';
import { DeleteWorkspaceDetails } from '@/models/WorkspaceDetails';
import { Workspace } from '@/models/WorkspacesByTenant';
import { selectWorkspaces } from '@/modules/Dashboard/Workspaces/workspaceSlice';
import queryKeys from '@/modules/shared/query/queryKeys';
import { AppRoutes } from '@/utils/routes/router';

const DeleteWorkspace = () => {
  const deleteWorkspaceMutation = useDeleteWorkspaceById();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { id: workspaceId } = useParams();
  const [toastInfo, setToastInfo] = useState({ message: '', toastType: '' });
  const workspaces = useAppSelector(selectWorkspaces);
  const workspaceToDelete = workspaces?.filter((workspace) => workspace.id === workspaceId);
  const queryClient = useQueryClient();

  useEffect(() => {
    let timerBeforeNavigation: NodeJS.Timeout;
    if (toastInfo.toastType === 'success') {
      timerBeforeNavigation = setTimeout(() => navigate(AppRoutes.workspaces), 2000);
    }
    return () => {
      clearTimeout(timerBeforeNavigation);
    };
  }, [navigate, toastInfo]);

  const schema = useMemo(() => {
    return yup
      .object()
      .shape({
        workspaceName: yup
          .string()
          .trim()
          .oneOf([workspaceToDelete[0]?.name], t('DeleteWorkspacePage.workspaceNameNotMatchingError'))
          .required(t('CommonErrors.workspaceNameRequired')),
      })
      .required();
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<DeleteWorkspaceDetails>({
    resolver: yupResolver(schema),
  });

  const onSubmit = (data: DeleteWorkspaceDetails) => {
    if (workspaceId && workspaces.length > 1) {
      const sendData = workspaceId;

      deleteWorkspaceMutation.mutate(sendData, {
        onSuccess: () => {
          queryClient.setQueryData<Workspace[]>(
            queryKeys.workspaces,
            (prevData) => prevData?.filter((workspace) => workspace.id !== workspaceId) || [],
          );
          setToastInfo({ message: t('DeleteWorkspacePage.toastSuccess'), toastType: 'success' });
          setOpen(true);
        },
        onError: () => {
          setToastInfo({ message: t('DeleteWorkspacePage.toastError'), toastType: 'error' });
          setOpen(true);
        },
      });
    } else if (workspaces.length === 1) {
      setToastInfo({ message: t('DeleteWorkspacePage.toastErrorLastWorkspace'), toastType: 'error' });
      setOpen(true);
    }
  };

  const onBack = () => {
    navigate(AppRoutes.workspaces);
  };

  return (
    <>
      <div className="flex flex-col items-center mt-12">
        <div>
          <div className="position: relative mb-4">
            <div className="position: absolute top-0.5 -left-10 cursor-pointer" onClick={onBack}>
              <ChevronIcon type={'left'} width={'24'} height={'24'} viewBox={'0 0 24 24'} />
            </div>
            <h1 className="text-custom-aliceBlue font-semibold text-xl mb-3">{t('DeleteWorkspacePage.title')}</h1>
            <p className="text-gray-900 py-5 px-6 bg-yellow-200 rounded-lg w-[50rem]">
              <Trans
                i18nKey="DeleteWorkspacePage.bannerLabel"
                components={{
                  primary: <span className="font-bold" />,
                }}
              />
            </p>
          </div>
          <form className="space-y-6 min-w-[50rem] max-w-[50rem] w-1/2 mx-auto" onSubmit={handleSubmit(onSubmit)}>
            <section className="px-6 pt-6 pb-8 space-y-8 shadow rounded-lg bg-custom-gray text-custom-aliceBlue">
              <p>
                <Trans
                  i18nKey="DeleteWorkspacePage.descriptionText"
                  components={{
                    primary: <span className="text-yellow-200" />,
                  }}
                  values={{ workspaceName: workspaceToDelete[0]?.name }}
                />
              </p>
              <section className="space-y-4">
                <p>{t('DeleteWorkspacePage.typeNameInstructionLabel')}</p>
                <Input
                  className="text-custom-aliceBlue border-gray-700"
                  data-cy="deleteWorkspaceName"
                  placeholder={t('DeleteWorkspacePage.workspaceNamePlaceholder')}
                  {...register('workspaceName')}
                  error={t(errors.workspaceName?.message) as string}
                />
                <p>{t('DeleteWorkspacePage.caseSensitiveLabel')}</p>
              </section>
            </section>
            <div className="flex justify-end">
              <Button variant="cancel" onClick={onBack}>
                {t('DeleteWorkspacePage.linkCancel')}
              </Button>
              <Button
                variant="destructive"
                className="px-4 !w-52 ml-8"
                isLoading={deleteWorkspaceMutation.isLoading || deleteWorkspaceMutation.isSuccess}
              >
                {t('DeleteWorkspacePage.buttonPermanentlyDelete')}
              </Button>
            </div>
          </form>
        </div>
        <ProvideToasts>
          <Toast
            isOpen={open}
            setOpen={setOpen}
            message={toastInfo.message}
            toastType={toastInfo.toastType as ToastType}
          />
        </ProvideToasts>
      </div>
    </>
  );
};

export default DeleteWorkspace;
